import type { ExcludesFalseyTypeGuard } from '../../../utils';
import type { DoubleClickEventData } from '../mouseStepAction';
import type { DispatchFocusFn } from './focusElement';
import type { IMouseEvent } from 'clickim/src/common/models/mouseStep';
import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';

declare function getLocatedElement(locatedElement: ILocatedElement | string): Element | null;
declare const dispatchFocus: DispatchFocusFn;

export const doubleClick = function (eventData: DoubleClickEventData, done: (data: { status: 'done'; success: boolean; result?: string }) => void) {
    const POINTER_EVENTS = ['pointerup', 'pointerdown', 'pointermove'];
    const element = getLocatedElement(eventData.locatedElement);

    if (!element) {
        throw new Error('element not found');
    }
    const events = eventData.events;

    const successObject = {
        status: 'done' as const,
        success: true,
    };

    window.__unloadNavigator = function () { done(successObject); };

    const getEventPosition = function (userEvent: IMouseEvent) {
        function isWithinBounds(start: number, end: number, point: number) {
            return (point > start) && (point < end);
        }

        const pointerPosition = userEvent.pointerPosition || {} as Partial<NonNullable<IMouseEvent['pointerPosition']>>;
        const rect = element.getBoundingClientRect();
        const clientX = pointerPosition.originX && isWithinBounds(rect.left, rect.left + rect.width, pointerPosition.originX) ? pointerPosition.originX : rect.left + (rect.width / 2);
        const clientY = pointerPosition.originY && isWithinBounds(rect.top, rect.top + rect.height, pointerPosition.originY) ? pointerPosition.originY : rect.top + (rect.height / 2);
        return { x: clientX, y: clientY };
    };

    const getPointerEventDictionary = function (clientX: number, clientY: number): PointerEventInit {
        return {
            screenX: 0,
            screenY: 0,
            clientX,
            clientY,
            ctrlKey: false,
            altKey: false,
            shiftKey: false,
            metaKey: false,
            bubbles: true,
            cancelable: true,
            button: 0,
            pointerType: 'mouse',
            isPrimary: true,
        };
    };

    const createPointerEvent = function (eventType: string, clientX: number, clientY: number) {
        if (!window.PointerEvent) {
            return undefined;
        }
        const eventProperties = getPointerEventDictionary(clientX, clientY);
        return new window.PointerEvent(eventType, eventProperties);
    };

    const createMouseEvent = function (eventType: string, clientX: number, clientY: number) {
        const event = document.createEvent('MouseEvents');
        event.initMouseEvent(
            eventType,
            true, /* bubbles */
            true, /* cancelable */
            document.defaultView!, /* view */
            1, /* detail */
            0, /* screenX */
            0, /* screenY */
            clientX, /* clientX */
            clientY, /* clientY */
            false, /* ctrl */
            false, /* alt */
            false, /* shift */
            false, /* meta */
            0, /* button */
            document.body ? document.body.parentNode : document.documentElement);
        return event;
    };

    const findEffectiveActiveElement = function () {
        let activeElement = document.activeElement!;
        while (activeElement.shadowRoot?.activeElement) {
            activeElement = activeElement.shadowRoot.activeElement;
        }
        return activeElement;
    };

    const getEvent = function (userEventData: IMouseEvent) {
        const pos = getEventPosition(userEventData);
        const eventType = userEventData.event;
        if (POINTER_EVENTS.includes(eventType)) {
            return createPointerEvent(eventType, pos.x, pos.y);
        }
        return createMouseEvent(eventType, pos.x, pos.y);
    };

    try {
        events
            .map((userEventData) => getEvent(userEventData))
            .filter(Boolean as any as ExcludesFalseyTypeGuard)
            .forEach((event) => element.dispatchEvent(event));
        const oldActiveElement = findEffectiveActiveElement();
        dispatchFocus(eventData.elementToFocusLocatedElement, oldActiveElement);
        if (window.__unloadNavigator) {
            window.removeEventListener('unload', window.__unloadNavigator);
            // @ts-expect-error badly typed
            window.__unloadNavigator = null;
        }
        done(successObject);
    } catch (e) {
        if (window.__unloadNavigator) {
            window.removeEventListener('unload', window.__unloadNavigator);
            // @ts-expect-error badly typed
            window.__unloadNavigator = null;
        }
        done({
            status: 'done',
            result: e.toString(),
            success: false,
        });
    }
};
